import { QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { RouterProvider } from "react-router";
import { Toaster } from "sonner";

import { createQueryClient } from "@/app/queryClient";
import { router } from "@/app/router";
import { SessionProvider } from "@/app/session/SessionProvider";
import { useSession } from "@/app/session/useSession";
import { LocaleProvider } from "@/shared/i18n/LocaleProvider";
import { useLocale } from "@/shared/i18n/useT";
import { directionOf } from "@/shared/i18n/messages";
import { RealtimeProvider } from "@/shared/realtime/RealtimeProvider";
import { ThemeProvider } from "@/shared/theme/ThemeProvider";
import { useTheme } from "@/shared/theme/useTheme";

/* Toasts in the interface's direction and theme; their look comes from the tokens (index.css). */
function LocalizedToaster() {
  const { locale } = useLocale();
  const { theme } = useTheme();
  return <Toaster position="top-center" closeButton theme={theme} dir={directionOf(locale)} />;
}

/* Live updates for whoever is signed in to the team app (client people use the portal). */
function LiveUpdates({ children }: { children: ReactNode }) {
  const { state } = useSession();
  const userId = state.status === "signed-in" && state.user.kind === "staff" ? state.user.id : null;
  return <RealtimeProvider userId={userId}>{children}</RealtimeProvider>;
}

export function App() {
  const [queryClient] = useState(createQueryClient);
  return (
    <ThemeProvider>
      <LocaleProvider>
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            <LiveUpdates>
              <RouterProvider router={router} />
              <LocalizedToaster />
            </LiveUpdates>
          </SessionProvider>
        </QueryClientProvider>
      </LocaleProvider>
    </ThemeProvider>
  );
}
